<!-- eslint-disable max-len -->
<template>
  <div>
    <!-- Billing Type Tabs -->
    <v-row class="mt-2">
      <v-col cols="12">
        <v-tabs v-model="activeTab" background-color="transparent" color="primary">
          <v-tab>
            <v-icon left>mdi-lightning-bolt</v-icon>
            {{ $t('billing.dashboard.electricityBilling') }}
          </v-tab>
          <v-tab>
            <v-icon left>mdi-engine</v-icon>
            {{ $t('billing.dashboard.dgBilling') }}
          </v-tab>
        </v-tabs>
      </v-col>
    </v-row>

    <!-- Quick Stats -->
    <v-row class="mt-4">
      <!-- Date Range Filter -->
      <v-col cols="12">
        <v-card outlined>
          <v-card-text class="py-2">
            <v-row align="center">
              <v-col cols="12" md="3">
                <v-select
                  v-model="selectedPeriod"
                  :items="periodOptions"
                  :label="$t('billing.dashboard.timePeriod')"
                  dense
                  outlined
                  hide-details
                  @change="updateDateRange"
                >
                  <template #prepend-inner>
                    <v-icon small>mdi-calendar-range</v-icon>
                  </template>
                </v-select>
              </v-col>
              <v-col cols="12" md="4">
                <v-menu
                  v-model="startDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template #activator="{ on, attrs }">
                    <v-text-field
                      v-model="formattedStartDate"
                      :label="$t('billing.dashboard.fromDate')"
                      prepend-inner-icon="mdi-calendar"
                      dense
                      outlined
                      readonly
                      hide-details
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    v-model="dateRange.start"
                    @input="onStartDateInput"
                  />
                </v-menu>
              </v-col>
              <v-col cols="12" md="4">
                <v-menu
                  v-model="endDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template #activator="{ on, attrs }">
                    <v-text-field
                      v-model="formattedEndDate"
                      :label="$t('billing.dashboard.toDate')"
                      prepend-inner-icon="mdi-calendar"
                      dense
                      outlined
                      readonly
                      hide-details
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    v-model="dateRange.end"
                    @input="onEndDateInput"
                  />
                </v-menu>
              </v-col>
              <v-col cols="12" md="1">
                <v-btn
                  icon
                  color="primary"
                  @click="resetDateRange"
                  :title="$t('billing.dashboard.resetToAllTime')"
                >
                  <v-icon>mdi-refresh</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="mt-2" style="flex-wrap: nowrap;">
      <!-- Electricity Stats -->
      <template v-if="activeTab === 0">
        <v-col class="d-flex" style="flex: 1 1 0; min-width: 0;">
          <v-card color="primary" dark class="fill-height" style="width:100%">
            <v-card-text class="d-flex align-center fill-height">
              <div class="flex-grow-1 stat-card-content">
                <div class="text-h5 font-weight-bold">
                  {{ filteredRecords.length }}
                </div>
                <div class="text-subtitle-2">
                  {{ $t('billing.dashboard.electricityRecords') }}
                </div>
                <div v-if="dateRange.start || dateRange.end" class="text-caption">
                  {{ getPeriodText() }}
                </div>
              </div>
              <v-icon size="36" class="ml-2 stat-card-icon">mdi-receipt-text</v-icon>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col class="d-flex" style="flex: 1 1 0; min-width: 0;">
          <v-card color="success" dark class="fill-height" style="width:100%">
            <v-card-text class="d-flex align-center fill-height">
              <div class="flex-grow-1 stat-card-content">
                <div class="text-h5 font-weight-bold">
                  {{ $formatLargeCurrency(filteredTotalAmount) }}
                </div>
                <div class="text-subtitle-2">
                  {{ $t('billing.dashboard.totalCalculated') }}
                </div>
                <div class="text-caption">
                  {{ $formatNumber(filteredAverageUnitPrice, 2) }}
                  {{ $currencySymbol }}/kWh {{ $t('billing.dashboard.avg') }}
                </div>
              </div>
              <v-icon size="36" class="ml-2 stat-card-icon">mdi-calculator</v-icon>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col class="d-flex" style="flex: 1 1 0; min-width: 0;">
          <v-card color="teal" dark class="fill-height" style="width:100%">
            <v-card-text class="d-flex align-center fill-height">
              <div class="flex-grow-1 stat-card-content">
                <div class="text-h5 font-weight-bold">
                  {{ $formatLargeCurrency(filteredTotalManualBill) }}
                </div>
                <div class="text-subtitle-2">
                  {{ $t('billing.dashboard.totalActualBill') }}
                </div>
                <div class="text-caption">
                  {{ $formatNumber(filteredAverageUnitPriceActualBill, 2) }}
                  {{ $currencySymbol }}/kWh {{ $t('billing.dashboard.avg') }}
                </div>
              </div>
              <v-icon size="36" class="ml-2 stat-card-icon">mdi-receipt</v-icon>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col class="d-flex" style="flex: 1 1 0; min-width: 0;">
          <v-card color="purple" dark class="fill-height" style="width:100%">
            <v-card-text class="d-flex align-center fill-height">
              <div class="flex-grow-1 stat-card-content">
                <div class="text-h5 font-weight-bold">
                  {{ $formatNumber(filteredCalculatedKwh) }}
                </div>
                <div class="text-subtitle-2">
                  {{ $t('billing.dashboard.calculatedKwh') }}
                </div>
                <div v-if="shouldShowAverage()" class="text-caption">
                  {{ getAverageCalculatedKwh() }}
                </div>
                <div v-else class="text-caption">{{ $t('billing.dashboard.fromSystem') }}</div>
              </div>
              <v-icon size="36" class="ml-2 stat-card-icon">mdi-calculator-variant</v-icon>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col class="d-flex" style="flex: 1 1 0; min-width: 0;">
          <v-card color="orange" dark class="fill-height" style="width:100%">
            <v-card-text class="d-flex align-center fill-height">
              <div class="flex-grow-1 stat-card-content">
                <div class="text-h5 font-weight-bold">
                  {{ $formatNumber(filteredActualKwhFromBill) }}
                </div>
                <div class="text-subtitle-2">
                  {{ $t('billing.dashboard.actualKwhFromBill') }}
                </div>
                <div v-if="shouldShowAverage()" class="text-caption">
                  {{ getAverageActualKwh() }}
                </div>
                <div v-else class="text-caption">
                  {{ $t('billing.dashboard.fromUtilityBill') }}
                </div>
              </div>
              <v-icon size="36" class="ml-2 stat-card-icon">mdi-file-document</v-icon>
            </v-card-text>
          </v-card>
        </v-col>
      </template>

      <!-- DG Stats -->
      <template v-if="activeTab === 1">
        <v-col class="d-flex" style="flex: 1 1 0; min-width: 0;">
          <v-card color="orange" dark class="fill-height" style="width:100%">
            <v-card-text class="d-flex align-center fill-height">
              <div class="flex-grow-1 stat-card-content">
                <div class="text-h5 font-weight-bold">
                  {{ filteredDGRecords.length }}
                </div>
                <div class="text-subtitle-2">{{ $t('billing.dashboard.dgRecords') }}</div>
                <div v-if="dateRange.start || dateRange.end" class="text-caption">
                  {{ getPeriodText() }}
                </div>
              </div>
              <v-icon size="36" class="ml-2 stat-card-icon">mdi-engine</v-icon>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col class="d-flex" style="flex: 1 1 0; min-width: 0;">
          <v-card color="deep-orange" dark class="fill-height" style="width:100%">
            <v-card-text class="d-flex align-center fill-height">
              <div class="flex-grow-1 stat-card-content">
                <div class="text-h5 font-weight-bold">
                  {{ $formatNumber(filteredTotalDGConsumption) }}
                </div>
                <div class="text-subtitle-2">{{ $t('billing.dashboard.totalDgKwh') }}</div>
                <div v-if="shouldShowAverage()" class="text-caption">
                  {{ getDGAverageConsumption() }}
                </div>
                <div v-else class="text-caption">
                  {{ filteredDGRecords.length }} {{ $t('billing.dashboard.records') }}
                </div>
              </div>
              <v-icon size="36" class="ml-2 stat-card-icon">mdi-lightning-bolt</v-icon>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col class="d-flex" style="flex: 1 1 0; min-width: 0;">
          <v-card color="brown" dark class="fill-height" style="width:100%">
            <v-card-text class="d-flex align-center fill-height">
              <div class="flex-grow-1 stat-card-content">
                <div class="text-h5 font-weight-bold">
                  {{ $formatNumber(filteredTotalDieselConsumption) }}
                </div>
                <div class="text-subtitle-2">{{ $t('billing.dashboard.totalDiesel') }}</div>
                <div class="text-caption">{{ $t('billing.dashboard.litres') }}</div>
              </div>
              <v-icon size="36" class="ml-2 stat-card-icon">mdi-gas-station</v-icon>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col class="d-flex" style="flex: 1 1 0; min-width: 0;">
          <v-card color="indigo" dark class="fill-height" style="width:100%">
            <v-card-text class="d-flex align-center fill-height">
              <div class="flex-grow-1 stat-card-content">
                <div class="text-h5 font-weight-bold">
                  {{ $formatNumber(filteredTotalRunHours) }}
                </div>
                <div class="text-subtitle-2">{{ $t('billing.dashboard.totalRunHours') }}</div>
                <div v-if="shouldShowAverage()" class="text-caption">
                  {{ getDGAverageRunHours() }}
                </div>
                <div v-else class="text-caption">
                  {{ filteredDGRecords.length }} {{ $t('billing.dashboard.records') }}
                </div>
              </div>
              <v-icon size="36" class="ml-2 stat-card-icon">mdi-clock-outline</v-icon>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col class="d-flex" style="flex: 1 1 0; min-width: 0;">
          <v-card color="teal" dark class="fill-height" style="width:100%">
            <v-card-text class="d-flex align-center fill-height">
              <div class="flex-grow-1 stat-card-content">
                <div class="text-h5 font-weight-bold">
                  {{ $formatNumber(filteredAverageEfficiency, 1) }}
                </div>
                <div class="text-subtitle-2">{{ $t('billing.dashboard.avgEfficiency') }}</div>
                <div class="text-caption">kWh/L</div>
              </div>
              <v-icon size="36" class="ml-2 stat-card-icon">mdi-speedometer</v-icon>
            </v-card-text>
          </v-card>
        </v-col>
      </template>
    </v-row>

    <!-- Recent Activity & Quick Actions -->
    <v-row class="mt-4">
      <!-- Recent Billing Records -->
      <v-col cols="12" :md="activeTab === 1 ? 8 : 12">
        <!-- Electricity Records -->
        <ResponsiveCard
          v-if="activeTab === 0"
          :title="$t('billing.dashboard.recentElectricityRecords')"
          title-icon="mdi-history"
          :show-refresh="true"
          :refreshing="loading"
          @refresh="refreshData"
        >
          <LoadingState
            v-if="loading && !recentRecords.length"
            type="skeleton"
            skeleton-type="table-row@3"
          />

          <EmptyState
            v-else-if="!recentRecords.length"
            :title="$t('billing.dashboard.noRecentRecords')"
            :message="$t('billing.dashboard.noBillingRecords')"
            icon="mdi-receipt-text-outline"
            icon-size="48"
            :show-create="true"
            :primary-action-text="$t('billing.dashboard.createBill')"
            @primary-action="$router.push('/billing-entry')"
          />

          <v-simple-table v-else dense>
            <template #default>
              <thead>
                <tr>
                  <th>{{ $t('billing.dashboard.period') }}</th>
                  <th class="text-right">{{ $t('billing.dashboard.calculated') }}</th>
                  <th class="text-right">{{ $t('billing.dashboard.actualBill') }}</th>
                  <th class="text-right">{{ $t('billing.dashboard.consumption') }}</th>
                  <th class="text-right">{{ $t('billing.dashboard.actualKwh') }}</th>
                  <th class="text-right">{{ $t('billing.dashboard.variance') }}</th>
                  <th>{{ $t('billing.dashboard.status') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="record in recentRecords" :key="record.id || record._id">
                  <td>
                    <div class="text-subtitle-2">{{ formatBillingPeriod(record.billingPeriod) }}</div>
                    <div class="text-caption grey--text">{{ $formatDate(record.createdAt) }}</div>
                  </td>
                  <td class="text-right">
                    <div class="text-subtitle-2">{{ $formatLargeCurrency(record.calculatedCharges?.totalCalculated || 0) }}</div>
                    <div class="text-caption grey--text">{{ $currencySymbol }}</div>
                  </td>
                  <td class="text-right">
                    <div class="text-subtitle-2">{{ $formatLargeCurrency(record.actualBillAmount || 0) }}</div>
                    <div class="text-caption grey--text">{{ $currencySymbol }}</div>
                  </td>
                  <td class="text-right">
                    <div class="text-subtitle-2">{{ $formatNumber(record.consumptionData?.totalConsumption || 0) }}</div>
                    <div class="text-caption grey--text">kWh</div>
                  </td>
                  <td class="text-right">
                    <div class="text-subtitle-2">{{ $formatNumber(record.actualKwhFromBill || 0) }}</div>
                    <div class="text-caption grey--text">kWh</div>
                  </td>
                  <td class="text-right">
                    <v-chip
                      small
                      :color="getVarianceColor(record.variance, record.calculatedCharges?.totalCalculated)"
                      text-color="white"
                    >
                      {{ $formatLargeCurrency(Math.abs(record.variance || 0)) }}
                    </v-chip>
                  </td>
                  <td>
                    <v-chip
                      small
                      :color="getStatusColor(record.variance, record.calculatedCharges?.totalCalculated)"
                      text-color="white"
                    >
                      {{ getStatusText(record.variance, record.calculatedCharges?.totalCalculated) }}
                    </v-chip>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <template #cardActions>
            <v-btn
              text
              color="primary"
              small
              @click="$router.push('/billing-app/billing-history')"
            >
              {{ $t('billing.dashboard.viewAll', { count: filteredRecords.length }) }}
              <v-icon right small>mdi-arrow-right</v-icon>
            </v-btn>
          </template>
        </ResponsiveCard>

        <!-- DG Records -->
        <ResponsiveCard
          v-if="activeTab === 1"
          :title="$t('billing.dashboard.recentDgRecords')"
          title-icon="mdi-engine"
          :show-refresh="true"
          :refreshing="dgLoading"
          @refresh="refreshDGData"
        >
          <LoadingState
            v-if="dgLoading && !recentDGRecords.length"
            type="skeleton"
            skeleton-type="table-row@3"
          />

          <EmptyState
            v-else-if="!recentDGRecords.length"
            :title="$t('billing.dashboard.noRecentDgRecords')"
            :message="$t('billing.dashboard.noDgBillingRecords')"
            icon="mdi-engine-outline"
            icon-size="48"
            :show-create="true"
            :primary-action-text="$t('billing.dashboard.createDgBill')"
            @primary-action="$router.push('/dg-billing-entry')"
          />

          <v-simple-table v-else dense>
            <template #default>
              <thead>
                <tr>
                  <th>{{ $t('billing.dashboard.period') }}</th>
                  <th class="text-right">DG kWh</th>
                  <th class="text-right">{{ $t('billing.dashboard.dieselL') }}</th>
                  <th class="text-right">{{ $t('billing.dashboard.runHours') }}</th>
                  <th class="text-right">{{ $t('billing.dashboard.efficiency') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="record in recentDGRecords" :key="record.id || record._id">
                  <td>
                    <div class="text-subtitle-2">{{ formatBillingPeriod(record.billingPeriod) }}</div>
                    <div class="text-caption grey--text">{{ $formatDate(record.createdAt) }}</div>
                  </td>
                  <td class="text-right">
                    <div class="text-subtitle-2">{{ $formatNumber(record.dgConsumptionData?.totalConsumption || 0) }}</div>
                    <div class="text-caption grey--text">kWh</div>
                  </td>
                  <td class="text-right">
                    <div class="text-subtitle-2">{{ $formatNumber(record.dieselConsumption || 0) }}</div>
                    <div class="text-caption grey--text">{{ $t('billing.dashboard.litres') }}</div>
                  </td>
                  <td class="text-right">
                    <div class="text-subtitle-2">{{ $formatNumber(record.runningHours || 0, 1) }}</div>
                    <div class="text-caption grey--text">{{ $t('billing.dashboard.hours') }}</div>
                  </td>
                  <td class="text-right">
                    <v-chip
                      small
                      :color="getEfficiencyColor(record.efficiencyMetrics?.kwhPerLitre || 0)"
                      text-color="white"
                    >
                      {{ $formatNumber(record.efficiencyMetrics?.kwhPerLitre || 0, 1) }} kWh/L
                    </v-chip>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <template #cardActions>
            <v-btn
              text
              color="primary"
              small
              @click="$router.push('/billing-app/dg-billing-history')"
            >
              {{ $t('billing.dashboard.viewAll', { count: filteredDGRecords.length }) }}
              <v-icon right small>mdi-arrow-right</v-icon>
            </v-btn>
          </template>
        </ResponsiveCard>
      </v-col>

      <!-- Quick Actions -->
      <v-col v-if="activeTab === 1" cols="12" md="4">
        <!-- DG Quick Actions -->
        <ResponsiveCard
          :title="$t('billing.dashboard.dgActions')"
          title-icon="mdi-engine-outline"
          :no-padding="true"
        >
          <v-list class="pa-0">
            <v-list-item
              dense
              link
              @click="$router.push('/billing-app/dg-billing-entry')"
            >
              <v-list-item-icon class="mr-3">
                <v-icon color="orange">mdi-plus-circle</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="text-body-2 font-weight-medium">
                  {{ $t('billing.dashboard.createDgBill') }}
                </v-list-item-title>
                <v-list-item-subtitle class="text-caption">
                  {{ $t('billing.dashboard.startNewDgBillingRecord') }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-icon small color="grey lighten-1">mdi-chevron-right</v-icon>
              </v-list-item-action>
            </v-list-item>

            <v-divider />

            <v-list-item
              dense
              link
              @click="$router.push('/billing-app/dg-billing-history')"
            >
              <v-list-item-icon class="mr-3">
                <v-icon color="deep-orange">mdi-history</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="text-body-2 font-weight-medium">
                  {{ $t('billing.dashboard.dgHistory') }}
                </v-list-item-title>
                <v-list-item-subtitle class="text-caption">
                  {{ $t('billing.dashboard.browseDgBillingRecords') }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-icon small color="grey lighten-1">mdi-chevron-right</v-icon>
              </v-list-item-action>
            </v-list-item>

            <v-divider />

            <v-list-item
              dense
              link
              @click="exportDGData"
            >
              <v-list-item-icon class="mr-3">
                <v-icon color="teal">mdi-download</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="text-body-2 font-weight-medium">
                  {{ $t('billing.dashboard.exportDgData') }}
                </v-list-item-title>
                <v-list-item-subtitle class="text-caption">
                  {{ $t('billing.dashboard.downloadDgReports') }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-icon small color="grey lighten-1">mdi-chevron-right</v-icon>
              </v-list-item-action>
            </v-list-item>

            <v-divider />

            <v-list-item
              dense
              link
              @click="showEfficiencyTrends"
            >
              <v-list-item-icon class="mr-3">
                <v-icon color="brown">mdi-speedometer</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="text-body-2 font-weight-medium">
                  {{ $t('billing.dashboard.efficiencyTrends') }}
                </v-list-item-title>
                <v-list-item-subtitle class="text-caption">
                  {{ $t('billing.dashboard.monitorDgPerformance') }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-icon small color="grey lighten-1">mdi-chevron-right</v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </ResponsiveCard>
      </v-col>
    </v-row>

    <!-- Efficiency Trends Dialog -->
    <v-dialog v-model="efficiencyTrendsDialog.show" max-width="800">
      <v-card v-if="efficiencyTrendsDialog.trends">
        <v-card-title class="primary white--text">
          <v-icon left color="white">mdi-speedometer</v-icon>
          DG Efficiency Trends Analysis
        </v-card-title>

        <v-card-text class="pt-4">
          <!-- Summary Stats -->
          <v-row>
            <v-col cols="12" md="6">
              <v-card outlined>
                <v-card-text class="text-center">
                  <div class="text-h4 primary--text">{{ efficiencyTrendsDialog.trends.avgEfficiency }}</div>
                  <div class="text-subtitle-2">Average Efficiency (kWh/L)</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="6">
              <v-card outlined>
                <v-card-text class="text-center">
                  <div class="text-h4" :class="getTrendColor(efficiencyTrendsDialog.trends.trend)">
                    {{ getTrendIcon(efficiencyTrendsDialog.trends.trend) }}
                  </div>
                  <div class="text-subtitle-2">Trend: {{ getTrendText(efficiencyTrendsDialog.trends.trend) }}</div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Performance Range -->
          <v-row class="mt-2">
            <v-col cols="12" md="4">
              <v-card outlined color="success lighten-4">
                <v-card-text class="text-center">
                  <div class="text-h5 success--text">{{ efficiencyTrendsDialog.trends.bestEfficiency }}</div>
                  <div class="text-caption">Best Performance (kWh/L)</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="4">
              <v-card outlined color="info lighten-4">
                <v-card-text class="text-center">
                  <div class="text-h5 info--text">{{ efficiencyTrendsDialog.trends.latestEfficiency }}</div>
                  <div class="text-caption">Latest Performance (kWh/L)</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="4">
              <v-card outlined color="warning lighten-4">
                <v-card-text class="text-center">
                  <div class="text-h5 warning--text">{{ efficiencyTrendsDialog.trends.worstEfficiency }}</div>
                  <div class="text-caption">Worst Performance (kWh/L)</div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Recent Records -->
          <div class="mt-4">
            <div class="text-subtitle-1 mb-2">
              <v-icon left>mdi-history</v-icon>
              Recent Performance (Last 5 Records)
            </div>
            <v-simple-table dense>
              <template #default>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th class="text-right">DG kWh</th>
                    <th class="text-right">Diesel (L)</th>
                    <th class="text-right">Efficiency</th>
                    <th class="text-center">Rating</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="record in efficiencyTrendsDialog.trends.records" :key="record.id">
                    <td>{{ $formatDate(record.billingPeriod.startDate) }}</td>
                    <td class="text-right">{{ $formatNumber(record.dgConsumptionData.totalConsumption) }}</td>
                    <td class="text-right">{{ $formatNumber(record.dieselConsumption) }}</td>
                    <td class="text-right">
                      <strong>{{ $formatNumber(record.efficiencyMetrics.kwhPerLitre, 2) }}</strong>
                    </td>
                    <td class="text-center">
                      <v-chip
                        small
                        :color="getEfficiencyColor(record.efficiencyMetrics.kwhPerLitre)"
                        text-color="white"
                      >
                        {{ getEfficiencyRating(record.efficiencyMetrics.kwhPerLitre) }}
                      </v-chip>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>

          <!-- Industry Comparison -->
          <v-alert
            type="info"
            outlined
            class="mt-4"
          >
            <div class="text-subtitle-2 mb-1">Industry Standard Comparison</div>
            <div class="text-body-2">
              Industry Standard: 3.0 - 3.5 kWh/L |
              Your Average: {{ efficiencyTrendsDialog.trends.avgEfficiency }} kWh/L
              <span v-if="parseFloat(efficiencyTrendsDialog.trends.avgEfficiency) >= 3.0" class="success--text">
                ✓ Meeting Standards
              </span>
              <span v-else class="warning--text">
                ⚠ Below Standards
              </span>
            </div>
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            text
            @click="exportEfficiencyTrends"
          >
            <v-icon left>mdi-download</v-icon>
            Export Trends
          </v-btn>
          <v-btn
            color="secondary"
            text
            @click="efficiencyTrendsDialog.show = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Global Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
      right
    >
      {{ snackbar.message }}
      <template #action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar.show = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
/* eslint-disable max-len */
import { mapGetters, mapActions } from 'vuex';
import ResponsiveCard from '../components/common/ResponsiveCard.vue';
import LoadingState from '../components/common/LoadingState.vue';
import EmptyState from '../components/common/EmptyState.vue';

import { DGExportService } from '../services/dgExportService';

export default {
  name: 'Dashboard',

  components: {
    ResponsiveCard,
    LoadingState,
    EmptyState,
  },

  data() {
    return {
      activeTab: 0, // 0 = Electricity, 1 = DG
      snackbar: {
        show: false,
        message: '',
        color: 'success',
        timeout: 5000,
      },
      efficiencyTrendsDialog: {
        show: false,
        trends: null,
      },
      // Date filtering
      selectedPeriod: 'last30',
      dateRange: {
        start: null,
        end: null,
      },
      startDateMenu: false,
      endDateMenu: false,
    };
  },

  computed: {
    ...mapGetters('billing', ['allBillingRecords', 'loading']),
    ...mapGetters('dgBilling', {
      allDGBillingRecords: 'allDGBillingRecords',
      dgLoading: 'loading',
    }),

    billingRecords() {
      return this.allBillingRecords || [];
    },

    dgBillingRecords() {
      return this.allDGBillingRecords || [];
    },

    periodOptions() {
      return [
        { text: this.$t('billing.dashboard.last7Days'), value: 'last7' },
        { text: this.$t('billing.dashboard.last30Days'), value: 'last30' },
        { text: this.$t('billing.dashboard.last3Months'), value: 'last90' },
        { text: this.$t('billing.dashboard.last6Months'), value: 'last180' },
        { text: this.$t('billing.dashboard.thisYear'), value: 'thisYear' },
        { text: this.$t('billing.dashboard.allTime'), value: 'all' },
        { text: this.$t('billing.dashboard.customRange'), value: 'custom' },
      ];
    },

    // Filtered records based on date range
    filteredRecords() {
      return this.filterRecordsByDate(this.billingRecords);
    },

    filteredDGRecords() {
      return this.filterRecordsByDate(this.dgBillingRecords);
    },

    // Recent records for display (limited to 10 for performance)
    recentRecords() {
      return this.filteredRecords
        .slice()
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .slice(0, 10);
    },

    recentDGRecords() {
      return this.filteredDGRecords
        .slice()
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .slice(0, 10);
    },

    // Filtered totals
    filteredTotalAmount() {
      return this.filteredRecords.reduce((sum, record) => sum + (record.calculatedCharges?.totalCalculated || 0), 0);
    },

    filteredTotalConsumption() {
      return this.filteredRecords.reduce((sum, record) => sum + (record.consumptionData?.totalConsumption || 0), 0);
    },

    filteredTotalManualBill() {
      return this.filteredRecords.reduce((sum, record) => sum + (record.actualBillAmount || 0), 0);
    },

    filteredTotalDGConsumption() {
      return this.filteredDGRecords.reduce((sum, record) => sum + (record.dgConsumptionData?.totalConsumption || 0), 0);
    },

    filteredTotalDieselConsumption() {
      return this.filteredDGRecords.reduce((sum, record) => sum + (record.dieselConsumption || 0), 0);
    },

    filteredTotalRunHours() {
      return this.filteredDGRecords.reduce((sum, record) => sum + (record.runningHours || 0), 0);
    },

    filteredAverageEfficiency() {
      if (this.filteredDGRecords.length === 0) return 0;
      const totalEfficiency = this.filteredDGRecords.reduce((sum, record) => sum + (record.efficiencyMetrics?.kwhPerLitre || 0), 0);
      return totalEfficiency / this.filteredDGRecords.length;
    },

    // Average unit price for electricity (total amount / total consumption)
    filteredAverageUnitPrice() {
      if (this.filteredTotalConsumption === 0) return 0;
      return this.filteredTotalAmount / this.filteredTotalConsumption;
    },

    filteredAverageUnitPriceActualBill() {
      if (this.filteredActualKwhFromBill === 0) return 0;
      return this.filteredTotalManualBill / this.filteredActualKwhFromBill;
    },

    // Calculated kWh (from system consumption data)
    filteredCalculatedKwh() {
      return this.filteredRecords.reduce((sum, record) => sum + (record.consumptionData?.totalConsumption || 0), 0);
    },

    // Actual kWh from Bill (from physical utility bills)
    filteredActualKwhFromBill() {
      return this.filteredRecords.reduce((sum, record) => sum + (record.actualKwhFromBill || 0), 0);
    },

    // Legacy computed properties for backward compatibility
    totalRecords() {
      return this.billingRecords.length;
    },

    totalDGRecords() {
      return this.dgBillingRecords.length;
    },

    totalAmount() {
      return this.billingRecords.reduce((sum, record) => sum + (record.calculatedCharges?.totalCalculated || 0), 0);
    },

    totalConsumption() {
      return this.billingRecords.reduce((sum, record) => sum + (record.consumptionData?.totalConsumption || 0), 0);
    },

    totalDGConsumption() {
      return this.dgBillingRecords.reduce((sum, record) => sum + (record.dgConsumptionData?.totalConsumption || 0), 0);
    },

    totalDieselConsumption() {
      return this.dgBillingRecords.reduce((sum, record) => sum + (record.dieselConsumption || 0), 0);
    },

    totalRunHours() {
      return this.dgBillingRecords.reduce((sum, record) => sum + (record.runningHours || 0), 0);
    },

    averageEfficiency() {
      if (this.dgBillingRecords.length === 0) return 0;
      const totalEfficiency = this.dgBillingRecords.reduce((sum, record) => sum + (record.efficiencyMetrics?.kwhPerLitre || 0), 0);
      return totalEfficiency / this.dgBillingRecords.length;
    },

    // Date formatting (using configuration)
    formattedStartDate() {
      return this.dateRange.start ? this.$formatDate(this.dateRange.start) : '';
    },

    formattedEndDate() {
      return this.dateRange.end ? this.$formatDate(this.dateRange.end) : '';
    },
  },

  async created() {
    await this.refreshData();
    await this.refreshDGData();
    // Set default date range to last 30 days
    this.updateDateRange();
  },

  methods: {
    ...mapActions('billing', ['fetchBillingRecords']),
    ...mapActions('dgBilling', {
      fetchDGBillingRecords: 'fetchDGBillingRecords',
    }),

    async refreshData() {
      try {
        await this.fetchBillingRecords();
      } catch (error) {
        console.error('Error refreshing data:', error);
        this.showMessage({
          type: 'error',
          message: 'Failed to refresh dashboard data',
        });
      }
    },

    async refreshDGData() {
      try {
        await this.fetchDGBillingRecords();
      } catch (error) {
        console.error('Error refreshing DG data:', error);
        this.showMessage({
          type: 'error',
          message: 'Failed to refresh DG dashboard data',
        });
      }
    },

    async exportDGData() {
      try {
        if (this.dgBillingRecords.length === 0) {
          this.showMessage({
            type: 'warning',
            message: 'No DG data to export',
          });
          return;
        }

        const filename = `dg_billing_export_${new Date().toISOString().split('T')[0]}.csv`;
        DGExportService.exportDGRecordsToCSV(this.dgBillingRecords, filename);

        this.showMessage({
          type: 'success',
          message: `Exported ${this.dgBillingRecords.length} DG records successfully`,
        });
      } catch (error) {
        console.error('Error exporting DG data:', error);
        this.showMessage({
          type: 'error',
          message: 'Failed to export DG data',
        });
      }
    },

    showEfficiencyTrends() {
      try {
        if (this.dgBillingRecords.length === 0) {
          this.showMessage({
            type: 'warning',
            message: 'No DG data available for efficiency analysis',
          });
          return;
        }

        // Calculate efficiency trends
        const trends = this.calculateEfficiencyTrends();

        // For now, show a summary dialog with efficiency trends
        // In the future, this could navigate to a dedicated trends page
        this.showEfficiencyTrendsDialog(trends);
      } catch (error) {
        console.error('Error calculating efficiency trends:', error);
        this.showMessage({
          type: 'error',
          message: 'Failed to calculate efficiency trends',
        });
      }
    },

    calculateEfficiencyTrends() {
      // Sort records by date
      const sortedRecords = this.dgBillingRecords
        .slice()
        .sort((a, b) => new Date(a.billingPeriod.startDate) - new Date(b.billingPeriod.startDate));

      // Calculate trends
      const efficiencies = sortedRecords.map((record) => record.efficiencyMetrics.kwhPerLitre);
      const avgEfficiency = efficiencies.reduce((sum, eff) => sum + eff, 0) / efficiencies.length;

      // Find best and worst efficiency
      const bestEfficiency = Math.max(...efficiencies);
      const worstEfficiency = Math.min(...efficiencies);

      // Calculate trend (simple linear trend)
      let trend = 'stable';
      if (efficiencies.length >= 3) {
        const recent = efficiencies.slice(-3).reduce((sum, eff) => sum + eff, 0) / 3;
        const older = efficiencies.slice(0, 3).reduce((sum, eff) => sum + eff, 0) / 3;

        if (recent > older + 0.2) trend = 'improving';
        else if (recent < older - 0.2) trend = 'declining';
      }

      return {
        totalRecords: sortedRecords.length,
        avgEfficiency: avgEfficiency.toFixed(2),
        bestEfficiency: bestEfficiency.toFixed(2),
        worstEfficiency: worstEfficiency.toFixed(2),
        trend,
        latestEfficiency: efficiencies[efficiencies.length - 1]?.toFixed(2) || '0',
        records: sortedRecords.slice(-5), // Last 5 records for display
      };
    },

    showEfficiencyTrendsDialog(trends) {
      // Show the trends in a proper dialog
      this.efficiencyTrendsDialog.trends = trends;
      this.efficiencyTrendsDialog.show = true;

      this.showMessage({
        type: 'info',
        message: `Efficiency analysis complete: ${this.getTrendText(trends.trend)} trend detected`,
      });
    },

    getTrendIcon(trend) {
      switch (trend) {
        case 'improving': return '📈';
        case 'declining': return '📉';
        default: return '➡️';
      }
    },

    getTrendText(trend) {
      switch (trend) {
        case 'improving': return 'Improving';
        case 'declining': return 'Declining';
        default: return 'Stable';
      }
    },

    getTrendColor(trend) {
      switch (trend) {
        case 'improving': return 'success--text';
        case 'declining': return 'error--text';
        default: return 'info--text';
      }
    },

    getEfficiencyRating(efficiency) {
      if (efficiency > 3.5) return 'Excellent';
      if (efficiency >= 3.0) return 'Good';
      if (efficiency >= 2.5) return 'Average';
      return 'Poor';
    },

    async exportEfficiencyTrends() {
      try {
        if (!this.efficiencyTrendsDialog.trends) return;

        const { trends } = this.efficiencyTrendsDialog;
        const filename = `dg_efficiency_trends_${new Date().toISOString().split('T')[0]}.csv`;

        // Create CSV data for trends
        const csvData = [
          ['DG Efficiency Trends Report'],
          ['Generated on', new Date().toLocaleString()],
          [''],
          ['SUMMARY'],
          ['Total Records', trends.totalRecords],
          ['Average Efficiency (kWh/L)', trends.avgEfficiency],
          ['Best Performance (kWh/L)', trends.bestEfficiency],
          ['Worst Performance (kWh/L)', trends.worstEfficiency],
          ['Latest Performance (kWh/L)', trends.latestEfficiency],
          ['Trend', this.getTrendText(trends.trend)],
          [''],
          ['RECENT PERFORMANCE'],
          ['Date', 'DG Generation (kWh)', 'Diesel Consumption (L)', 'Efficiency (kWh/L)', 'Rating'],
          ...trends.records.map((record) => [
            this.$formatDate(record.billingPeriod.startDate),
            record.dgConsumptionData.totalConsumption,
            record.dieselConsumption,
            record.efficiencyMetrics.kwhPerLitre.toFixed(2),
            this.getEfficiencyRating(record.efficiencyMetrics.kwhPerLitre),
          ]),
        ];

        // Convert to CSV and download
        const csvContent = csvData.map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');

        if (link.download !== undefined) {
          const url = URL.createObjectURL(blob);
          link.setAttribute('href', url);
          link.setAttribute('download', filename);
          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }

        this.showMessage({
          type: 'success',
          message: 'Efficiency trends exported successfully',
        });
      } catch (error) {
        console.error('Error exporting efficiency trends:', error);
        this.showMessage({
          type: 'error',
          message: 'Failed to export efficiency trends',
        });
      }
    },

    // Date filtering methods
    updateDateRange() {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const todayIso = today.toISOString().split('T')[0];
      const getPastDateIso = (days) => new Date(
        today.getTime() - days * 24 * 60 * 60 * 1000,
      ).toISOString().split('T')[0];

      switch (this.selectedPeriod) {
        case 'last7':
          this.dateRange.start = getPastDateIso(7);
          this.dateRange.end = todayIso;
          break;
        case 'last30':
          this.dateRange.start = getPastDateIso(30);
          this.dateRange.end = todayIso;
          break;
        case 'last90':
          this.dateRange.start = getPastDateIso(90);
          this.dateRange.end = todayIso;
          break;
        case 'last180':
          this.dateRange.start = getPastDateIso(180);
          this.dateRange.end = todayIso;
          break;
        case 'thisYear':
          [this.dateRange.start] = new Date(now.getFullYear(), 0, 1).toISOString().split('T');
          this.dateRange.end = todayIso;
          break;
        case 'all':
          this.dateRange.start = null;
          this.dateRange.end = null;
          break;
        case 'custom':
          // Keep existing dates or set to null
          break;
        default:
          break;
      }

      // Trigger reactivity for filtered data
      this.filterData();
    },

    resetDateRange() {
      this.selectedPeriod = 'all';
      this.dateRange.start = null;
      this.dateRange.end = null;
      this.filterData();
    },

    filterData() {
      // Force reactivity by updating a reactive property
      // This ensures computed properties recalculate when dates change
      this.$forceUpdate();
    },

    onStartDateInput() {
      this.startDateMenu = false;
      this.filterData();
    },

    onEndDateInput() {
      this.endDateMenu = false;
      this.filterData();
    },

    filterRecordsByDate(records) {
      if (!this.dateRange.start && !this.dateRange.end) {
        return records;
      }

      return records.filter((record) => {
        // Use billingPeriod.startDate as primary filter field, fallback to createdAt
        let recordDate;
        if (record.billingPeriod && record.billingPeriod.startDate) {
          recordDate = new Date(record.billingPeriod.startDate);
        } else if (record.createdAt) {
          recordDate = new Date(record.createdAt);
        } else {
          return true; // Include records without dates
        }

        // Normalize dates to avoid timezone issues
        const normalizeDate = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

        const normalizedRecordDate = normalizeDate(recordDate);

        if (this.dateRange.start) {
          const startDate = normalizeDate(new Date(this.dateRange.start));
          if (normalizedRecordDate < startDate) return false;
        }

        if (this.dateRange.end) {
          const endDate = normalizeDate(new Date(this.dateRange.end));
          if (normalizedRecordDate > endDate) return false;
        }

        return true;
      });
    },

    getPeriodText() {
      if (!this.dateRange.start && !this.dateRange.end) return 'All Time';
      if (this.dateRange.start && this.dateRange.end) {
        return `${this.$formatDate(this.dateRange.start)} - ${this.$formatDate(this.dateRange.end)}`;
      }
      if (this.dateRange.start) return `From ${this.$formatDate(this.dateRange.start)}`;
      if (this.dateRange.end) return `Until ${this.$formatDate(this.dateRange.end)}`;
      return '';
    },

    getAverageConsumption() {
      if (this.filteredRecords.length === 0) return '0 kWh/month';
      const monthsSpan = this.getMonthsSpan();
      const average = this.filteredTotalConsumption / monthsSpan;
      return `${this.$formatNumber(average)} kWh/month`;
    },

    getAverageCalculatedKwh() {
      if (this.filteredRecords.length === 0) return '0 kWh/month';
      const monthsSpan = this.getMonthsSpan();
      const average = this.filteredCalculatedKwh / monthsSpan;
      return `${this.$formatNumber(average)} kWh/month`;
    },

    getAverageActualKwh() {
      if (this.filteredRecords.length === 0) return '0 kWh/month';
      const monthsSpan = this.getMonthsSpan();
      const average = this.filteredActualKwhFromBill / monthsSpan;
      return `${this.$formatNumber(average)} kWh/month`;
    },

    getDGAverageConsumption() {
      if (this.filteredDGRecords.length === 0) return '0 kWh/month';
      const monthsSpan = this.getMonthsSpan();
      const average = this.filteredTotalDGConsumption / monthsSpan;
      return `${this.$formatNumber(average)} kWh/month`;
    },

    getDGAverageRunHours() {
      if (this.filteredDGRecords.length === 0) return '0 hrs/month';
      const monthsSpan = this.getMonthsSpan();
      const average = this.filteredTotalRunHours / monthsSpan;
      return `${this.$formatNumber(average)} hrs/month`;
    },

    shouldShowAverage() {
      // Only show average if we have a meaningful date range (more than 7 days)
      if (!this.dateRange.start || !this.dateRange.end) return false;
      const start = new Date(this.dateRange.start);
      const end = new Date(this.dateRange.end);
      const days = Math.floor((end - start) / (1000 * 60 * 60 * 24));
      return days > 7;
    },

    getMonthsSpan() {
      if (!this.dateRange.start || !this.dateRange.end) return 1;
      const start = new Date(this.dateRange.start);
      const end = new Date(this.dateRange.end);

      // Calculate months difference
      const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());

      // If less than a month, calculate based on days
      if (months === 0) {
        const days = Math.floor((end - start) / (1000 * 60 * 60 * 24));
        return Math.max(0.1, days / 30); // Minimum 0.1 month to avoid division by zero
      }

      return Math.max(1, months);
    },

    getVarianceStats() {
      if (this.filteredRecords.length === 0) return 'No data';
      const goodVariance = this.filteredRecords.filter((record) => {
        const variance = Math.abs(record.variance || 0);
        const calculated = record.calculatedCharges?.totalCalculated || 1;
        return ((variance / calculated) * 100) <= 5;
      }).length;
      const percentage = Math.round((goodVariance / this.filteredRecords.length) * 100);
      return `${percentage}% good variance`;
    },

    getVarianceColor(variance, calculatedAmount) {
      if (!calculatedAmount || calculatedAmount === 0) return 'grey';
      const percentage = Math.abs(((variance || 0) / calculatedAmount) * 100);
      if (percentage < 1) return 'success';
      if (percentage <= 5) return 'warning';
      return 'error';
    },

    getStatusColor(variance, calculatedAmount) {
      return this.getVarianceColor(variance, calculatedAmount);
    },

    getStatusText(variance, calculatedAmount) {
      if (!calculatedAmount || calculatedAmount === 0) return 'Unknown';
      const percentage = Math.abs(((variance || 0) / calculatedAmount) * 100);
      if (percentage < 1) return 'Excellent';
      if (percentage <= 5) return 'Good';
      return 'Review';
    },

    getEfficiencyColor(efficiency) {
      if (!efficiency) return 'grey';
      if (efficiency >= 3.5) return 'success';
      if (efficiency >= 3.0) return 'info';
      if (efficiency >= 2.5) return 'warning';
      return 'error';
    },

    showMessage({ type, message }) {
      const colorMap = {
        error: 'error',
        warning: 'warning',
        info: 'info',
      };

      this.snackbar = {
        show: true,
        message,
        color: colorMap[type] || 'success',
        timeout: type === 'error' ? 8000 : 5000,
      };
    },

    formatBillingPeriod(period) {
      if (!period || !period.startDate || !period.endDate) return 'N/A';
      const start = this.$formatDate(period.startDate, { month: 'short', day: 'numeric' });
      const end = this.$formatDate(period.endDate, { month: 'short', day: 'numeric' });
      return `${start} - ${end}`;
    },
  },
};
</script>

<style scoped>
.stat-card-content {
  min-width: 0;
  overflow: hidden;
}

.stat-card-content .text-h5 {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.stat-card-icon {
  flex-shrink: 0;
  opacity: 0.8;
}
</style>
